import { z } from 'zod';
import { AiHarnessVersion } from './ai-harness-version.js';
import { AiHarnessModelProfilesJson } from './ai-harness-profile.js';
/**
 * Cấu hình runtime của hệ thống — feature flag và tuỳ chọn mặc định.
 *
 * Nằm trong DB, do admin sửa qua UI, **không** phải biến môi trường: dự án giữ
 * đúng 5 biến env và mọi thứ đổi được lúc chạy đều phải đổi được mà không
 * redeploy (ADR-003, `apps/api/src/config/env.ts`).
 *
 * ## Vì sao có sổ đăng ký thay vì bảng key-value tự do
 *
 * Một bảng `(key, value JSON)` không ràng buộc gì sẽ biến thành bãi rác trong
 * sáu tháng: `feature.ai`, `featureAiEnabled`, `enable_ai` cùng tồn tại, không
 * ai biết cái nào đang có tác dụng, và một giá trị sai kiểu chỉ lộ ra khi tính
 * năng im lặng ngừng hoạt động. Sổ đăng ký dưới đây là nguồn sự thật: khoá lạ
 * bị API từ chối, giá trị sai kiểu bị Zod chặn, và giá trị mặc định luôn có
 * kể cả khi chưa ai từng đặt.
 */
export const WbsGranularity = z.enum(['coarse', 'medium', 'fine']);
/** Một hành động mà job mô phỏng vừa thực hiện — APM-1318c. */
export const SimulatedAction = z.enum(['start', 'finish', 'comment', 'create']);
/**
 * Tình trạng job mô phỏng hoạt động demo, cho màn hình quản trị — APM-1318c.
 *
 * Mọi trường đều `nullable` chứ không optional: "chưa chạy lần nào" là trạng
 * thái bình thường ngay sau khi khởi động lại, và màn hình phải nói ra điều đó
 * thay vì hiện một khoảng trống trông như lỗi tải.
 */
export const DemoActivityStatus = z.object({
    /** Công tắc `demo.simulate_activity` đang bật hay không. */
    enabled: z.boolean(),
    /** Có tổ chức trưng bày để mô phỏng hay không. */
    hasDemoOrg: z.boolean(),
    /** Số hành động mỗi lượt, theo cấu hình hiện tại. */
    perRun: z.number().int(),
    /** Phút giữa hai lượt. Hằng số trong code, hiện ra để admin biết nhịp. */
    intervalMinutes: z.number().int(),
    lastRunAt: z.iso.datetime().nullable(),
    lastActions: z.array(SimulatedAction).nullable(),
    /** Lý do lượt gần nhất không làm gì, hoặc `null`. */
    lastSkippedReason: z.enum(['disabled', 'no_demo_org']).nullable(),
    /**
     * Tổng số hành động job đã sinh ra kể từ lần khởi động này.
     *
     * Đếm trong bộ nhớ chứ không truy ngược `event_log`: hành động mô phỏng ghi
     * event **giống hệt** hành động của người thật (đó là chủ đích — xem
     * `SimulateDemoActivityJob`), nên không có cách nào đếm lại chúng từ DB mà
     * không tự đoán mò. Về 0 sau mỗi lần restart, và như thế là đúng nghĩa.
     */
    actionsSinceStart: z.number().int(),
});
/**
 * Một lượt dựng lại tổ chức trưng bày — APM-DEMO-o.
 *
 * ## Vì sao không có bảng riêng
 *
 * Lượt reset **là** một sự kiện của tổ chức, nên nó nằm ở `event_log` như mọi
 * sự kiện khác (`demo.reset`, aggregate `organization`). Một bảng riêng sẽ là
 * cuốn sổ lịch sử thứ hai cạnh cuốn đã có — và `DropDemoCommand` chỉ xoá event
 * **theo `project_id`**, nên event cấp tổ chức sống sót qua chính lượt dọn mà
 * nó ghi lại. Đó là điều kiện để cuốn sổ này có nghĩa.
 */
export const DemoResetRun = z.object({
    at: z.iso.datetime(),
    /** `cron` là lịch 30 phút; `manual` là admin bấm nút. */
    trigger: z.enum(['cron', 'manual']),
    projectsDropped: z.number().int().nonnegative(),
    projectsSeeded: z.number().int().nonnegative(),
    /**
     * Tổng số event của dự án demo bị xoá — gồm cả event do chính lượt gieo
     * trước sinh ra (~2000 mỗi bộ), nên **không** đọc con số này là "thay đổi
     * của người dùng".
     */
    eventsDropped: z.number().int().nonnegative(),
    /**
     * Thay đổi ghi **sau** lượt gieo trước, tức phần thật sự bị cuốn đi.
     *
     * `null` khi chưa có mốc nước để so (lượt đầu tiên sau khi nâng cấp, hoặc
     * sau khi lịch sử cũ đã bị dọn theo tuổi). Không điền 0 vào chỗ ấy: 0 nghĩa
     * là "không ai đụng vào", còn đây là "không biết" — hai câu khác hẳn nhau.
     *
     * ⚠️ Bao gồm cả hành động của job mô phỏng: nó ghi event **giống hệt** người
     * thật, có chủ đích, nên không tách được ở tầng dữ liệu.
     */
    changesSinceLastSeed: z.number().int().nonnegative().nullable(),
    durationMs: z.number().int().nonnegative(),
});
/** Tình trạng hai cỗ máy giữ tổ chức trưng bày luôn tươi — APM-DEMO-o. */
export const DemoMaintenanceStatus = z.object({
    hasDemoOrg: z.boolean(),
    /** Công tắc `demo.auto_reset`. */
    autoResetEnabled: z.boolean(),
    /** Phút giữa hai lượt dựng lại. Hằng số trong code. */
    resetIntervalMinutes: z.number().int(),
    /** Lượt gần nhất trước, cũ dần về sau. */
    runs: z.array(DemoResetRun),
});
/** Mỗi mục: schema để validate, giá trị mặc định, và mô tả cho màn hình admin. */
export const SYSTEM_SETTINGS = {
    'feature.ai_suggestions': {
        schema: z.boolean(),
        default: true,
        group: 'feature',
        label: 'Gợi ý từ AI',
        description: 'Tắt thì mọi tính năng sinh gợi ý bằng AI ngừng hoạt động. Dữ liệu đã sinh vẫn giữ nguyên.',
    },
    'feature.replay': {
        schema: z.boolean(),
        default: true,
        group: 'feature',
        label: 'Replay lịch sử dự án',
        description: 'Tua lại trạng thái dự án theo thời gian. Tắt để giảm tải khi dữ liệu rất lớn.',
    },
    'feature.demo_data': {
        schema: z.boolean(),
        default: true,
        group: 'feature',
        label: 'Sinh dữ liệu demo cho người dùng mới',
        description: 'Hai dự án mẫu khi đăng ký. Dữ liệu demo không bao giờ vào velocity hay báo cáo hiệu suất thật.',
    },
    /**
     * Mô phỏng hoạt động trong tổ chức trưng bày — APM-1318c.
     *
     * Vì sao mặc định **bật**: tổ chức demo chỉ tồn tại khi ai đó cố ý chạy
     * `pnpm seed:demo-org`, và job này tự thoát khi không có tổ chức nào như thế.
     * Nên với hệ thống không dùng demo, mặc định bật hay tắt đều không khác gì —
     * còn với hệ thống có dùng, tắt mặc định nghĩa là bộ trưng bày nằm im cho tới
     * khi ai đó tình cờ tìm ra công tắc này.
     */
    'demo.simulate_activity': {
        schema: z.boolean(),
        default: true,
        group: 'feature',
        label: 'Mô phỏng hoạt động trong tổ chức demo',
        description: 'Định kỳ tạo việc, đổi trạng thái và bình luận trong tổ chức trưng bày để nó trông như đang chạy thật. Không đụng tới tổ chức nào khác.',
    },
    /**
     * Dựng lại tổ chức trưng bày theo lịch — APM-DEMO-o.
     *
     * Tắt công tắc này là **đóng băng** bộ trưng bày: mọi thứ người xem thử (và
     * quản trị viên) sửa sẽ ở lại. Cái giá đi kèm phải nói ra ở màn hình: kịch
     * bản demo khai mọi mốc bằng độ lệch so với "hôm nay" và chỉ lượt dựng lại
     * mới neo chúng vào thời gian thật, nên đóng băng lâu ngày thì dự án demo
     * già đi — biểu đồ tiến độ trượt dần về quá khứ.
     *
     * Nút "Dựng lại ngay" **không** bị công tắc này chặn: nó là một hành động có
     * người bấm, và người ấy vừa đọc đúng cảnh báo ở trên.
     */
    'demo.auto_reset': {
        schema: z.boolean(),
        default: true,
        group: 'feature',
        label: 'Tự dựng lại tổ chức demo theo lịch',
        description: 'Cứ 30 phút xoá sạch dự án của tổ chức trưng bày rồi gieo lại từ kịch bản. Tắt thì mọi thay đổi ở lại, nhưng dữ liệu demo sẽ già dần vì không còn được neo lại vào hôm nay.',
    },
    /**
     * Số hành động mỗi lượt, không phải khoảng cách giữa các lượt.
     *
     * Lịch chạy là hằng số trong code (`SIMULATE_DEMO_ACTIVITY_CRON`). Cho admin
     * sửa nhịp cron qua UI nghĩa là một ô nhập sai cú pháp làm chết scheduler,
     * còn một con số thì cùng lắm là nhiều hoạt động hơn mong muốn — và trần 20
     * chặn cả điều đó.
     */
    'demo.activity_per_run': {
        schema: z.number().int().min(1).max(20),
        default: 3,
        group: 'feature',
        label: 'Số hành động mỗi lượt mô phỏng',
        description: 'Càng cao thì tổ chức demo càng nhộn nhịp. Chỉ có tác dụng khi công tắc trên bật.',
    },
    'wbs.default_granularity': {
        schema: WbsGranularity,
        default: 'medium',
        group: 'ai',
        label: 'Độ chi tiết WBS mặc định',
        description: 'Mức chia nhỏ mặc định khi AI sinh cây công việc. PM vẫn đổi được cho từng lần sinh.',
    },
    /**
     * Tầng `standard` của `AiModelTier`, và cũng là model cho lời gọi không khai
     * tầng nào. Giữ tên cũ `ai.default_model` thay vì đổi thành `ai.model.standard`:
     * đổi khoá là mất giá trị admin đã đặt, và "mặc định" đúng là nghĩa của nó.
     */
    'ai.default_model': {
        schema: z.string().min(1).max(60),
        /** Opus 5.5 từ APM-MDL-a: mới hơn và rẻ hơn Opus 5 ($4/$20 so với $5/$25). */
        default: 'claude-opus-5-5',
        group: 'ai',
        label: 'Model mặc định (tầng standard)',
        description: 'Phải có dòng giá tương ứng trong bảng giá, nếu không mọi lời gọi sẽ bị từ chối.',
    },
    'ai.model.strong': {
        schema: z.string().min(1).max(60),
        default: 'claude-opus-5-5',
        group: 'ai',
        label: 'Model cho việc khó (tầng strong)',
        description: 'Dùng cho sinh WBS và phân tích ảnh hưởng Change Request — output dài, cần tool use tốt.',
    },
    'ai.model.small': {
        schema: z.string().min(1).max(60),
        /**
         * Bút danh ngắn, **khớp với `ai_model_pricing`** (`APM-301` seed
         * `claude-haiku-4-5`). Mặc định không có dòng giá nghĩa là mọi lời gọi tầng
         * `small` bị từ chối ngay khi cài — và lỗi đó chỉ lộ ra lúc ai đó bấm nút.
         */
        default: 'claude-haiku-4-5',
        group: 'ai',
        label: 'Model cho việc nhẹ (tầng small)',
        description: 'Dùng cho phân loại và trích xuất — chạy nhiều lần nên cần rẻ. Phải có dòng giá tương ứng.',
    },
    // ── Quy chuẩn công việc — APM-1308a ───────────────────────────────────────
    //
    // Con số mặc định lấy theo chuẩn có thật, và nguồn ghi ngay trong mô tả để
    // người sửa biết mình đang rời khỏi cái gì:
    //
    //   - **8/80 của PMBOK**: một gói việc từ 8 đến 80 giờ. Dưới 8 thì chi phí
    //     theo dõi lớn hơn bản thân việc; trên 80 thì không ai biết nó đang ở đâu
    //     cho tới lúc trễ.
    //   - **Scrum thực hành phổ biến**: hạng mục lọt gọn trong một sprint hai
    //     tuần, và nhìn thấy tiến độ mỗi ngày ⇒ trần 5 ngày làm việc.
    //   - **Hai người là pair, ba trở lên là họp**: phối hợp tốn hơn phần việc
    //     tiết kiệm được. Trong mô hình này một task có MỘT người phụ trách, nên
    //     con số đếm là số người khác nhau trên các subtask của cùng một hạng mục.
    //
    // ⚠️ Tất cả đều CẢNH BÁO, không chặn. Quy chuẩn sai trong 5% trường hợp là
    // chuyện thường; một cửa chặn sẽ khiến người dùng nhập số giả để đi qua, và
    // khi đó dữ liệu ước lượng — thứ nuôi velocity ở mục 10 — thành rác.
    // Ngoại lệ có chủ ý: thiếu hạn / ước lượng giờ bị CHẶN khi dự án bật
    // `requirePlanBeforeStart` (APM-EREF-a, người dùng đổi 2026-10-13) — đường qua là
    // ước lượng theo việc mẫu đã làm thật, xem docs/plan-estimate-by-reference.md.
    /**
     * Chính sách tổ chức cho vòng lặp agent — APM-H-c, `docs/plan-harness.md` §7.
     *
     * Mỗi khoá là một **trần**; skill chỉ được xin chặt hơn. Mặc định ở đây cũng
     * là mặc định của harness (`policy/defaults.ts` đọc lại từ chính sổ này), nên
     * một con số chỉ có một chỗ để đổi.
     */
    'ai.policy.max_turns': {
        schema: z.number().int().min(1).max(20),
        default: 6,
        group: 'ai_policy',
        label: 'Số lượt gọi model tối đa mỗi lần chạy',
        description: 'Một lần chạy AI được gọi model bấy nhiêu lượt (đọc dữ liệu, sửa kết quả). Chạm trần là dấu hiệu prompt hoặc bộ công cụ chưa đúng, không phải lý do để nới.',
    },
    'ai.policy.max_identical_tool_calls': {
        schema: z.number().int().min(1).max(50),
        default: 3,
        group: 'ai_policy',
        label: 'Số lần gọi lặp cùng công cụ cùng tham số trong một lần chạy',
        description: 'Lần thứ hai trở đi trả lại kết quả đã có (memo). Vượt trần là model đang mò: lần chạy kết thúc bằng một lượt kết với kết quả một phần.',
    },
    'ai.policy.max_calls_per_tool': {
        schema: z.number().int().min(1).max(200),
        default: 12,
        group: 'ai_policy',
        label: 'Số lần gọi một công cụ tối đa trong một lần chạy',
        description: 'Bất kể tham số. Vượt trần ⇒ lượt kết với kết quả một phần.',
    },
    'ai.policy.rate_per_user_per_minute': {
        schema: z.number().int().min(0).max(1000),
        default: 20,
        group: 'ai_policy',
        label: 'Số lần chạy AI tối đa mỗi người mỗi phút',
        description: '0 = không giới hạn. Vượt ⇒ 429 kèm thời gian chờ. Đếm trong bộ nhớ của một instance.',
    },
    'ai.policy.rate_per_skill_per_user_per_hour': {
        schema: z.number().int().min(0).max(10_000),
        default: 60,
        group: 'ai_policy',
        label: 'Số lần chạy một tính năng AI tối đa mỗi người mỗi giờ',
        description: '0 = không giới hạn. Chặn một người bấm đi bấm lại cùng một tính năng.',
    },
    'ai.policy.redaction': {
        schema: z.boolean(),
        default: false,
        group: 'ai_policy',
        label: 'Ẩn danh tên và email thành viên trước khi gửi model',
        description: 'Bật thì model chỉ thấy "Người A", "Người B" và kết quả được ánh xạ ngược khi nhận. Gợi ý giao việc kém đi vì model không biết ai đã làm gì. Tắt mặc định.',
    },
    /*
      Ablation — APM-H-ad.
  
      Danh sách **tên hook** (`KnowledgeHook`, `ContextHook`, …) bị gỡ khỏi chuỗi,
      phân cách dấu phẩy. **Chỉ hiệu lực ở `mode: sandbox`**: một cờ tắt
      `KnowledgeHook` rò ra production là một lượt chạy thiếu kiến thức tổ chức mà
      không ai biết — nó không báo lỗi, nó chỉ trả lời tệ hơn.
  
      Danh sách trắng ở `ABLATABLE_HOOKS`: tắt được là những hook **thêm** hành vi
      (kiến thức, bộ nhớ, ẩn danh, memo, suy nghĩ, thanh trạng thái); không tắt
      được là những hook **giữ hàng rào** (quyền, bọc dữ liệu, verify, ngân sách,
      timeout, dấu vết, lượt kết, prompt).
    */
    'ai.policy.disabled_hooks': {
        schema: z.string().max(200),
        default: '',
        group: 'ai_policy',
        label: 'Hook bị tắt khi chạy sandbox (đo đóng góp)',
        description: 'Tên hook phân cách dấu phẩy, ví dụ "KnowledgeHook,MemoHook". Chỉ áp cho lượt chạy sandbox, và bước `policy` ghi lại. Tên không có trong danh sách tắt được thì bị bỏ qua — hàng rào không tắt được bằng một ô cấu hình.',
    },
    'ai.harness.version': {
        schema: AiHarnessVersion,
        default: 'v2',
        group: 'ai_policy',
        label: 'Phiên bản harness',
        description: 'v1: vòng lặp gốc. v2: hỗ trợ model nhỏ và nhiều hãng. v3: quy trình có kiểm soát. v4: trao đổi ngắn gọn và phản biện có cấu trúc (thử nghiệm). Đổi chỉ áp dụng cho lượt chạy mới; lượt chạy tiếp giữ phiên bản cũ. Lịch sử: GET /ai/harness/versions.',
    },
    'ai.harness.default_profile': {
        schema: z.enum(['auto', 'native', 'guided', 'json']),
        default: 'auto',
        group: 'ai_policy',
        label: 'Chế độ tương thích model của harness',
        description: 'auto: tầng small dùng guided, tầng khác dùng native. guided hướng dẫn từng bước; json dùng văn bản JSON khi model không hỗ trợ gọi tool. Hồ sơ theo model được ưu tiên.',
    },
    'ai.harness.model_profiles': {
        schema: AiHarnessModelProfilesJson,
        default: '[]',
        group: 'ai_policy',
        label: 'Hồ sơ harness theo model (JSON)',
        description: 'Mảng các mục {provider, modelId, profile, maxOutputTokens?}. profile là native, guided hoặc json. Khớp chính xác tên model tại nhà cung cấp, áp lại khi chuyển sang model dự phòng. Giới hạn token là tùy chọn; không thay đổi giá hay quyền truy cập.',
    },
    'ai.runner': {
        schema: z.enum(['internal', 'deerflow']),
        default: 'internal',
        group: 'ai',
        label: 'Runner của harness AI',
        description: 'internal = vòng lặp tự xây (mặc định). deerflow = adapter độc lập ở instance khác — chưa triển khai; đặt vào là mọi lượt AI báo lỗi rõ thay vì âm thầm chạy internal (APM-H-r).',
    },
    'ai.policy.max_reasoning_strong': {
        schema: z.enum(['off', 'low', 'medium', 'high', 'adaptive']),
        default: 'adaptive',
        group: 'ai_policy',
        label: 'Trần mức suy nghĩ cho tầng model mạnh',
        description: 'Skill xin cao hơn trần bị kẹp về trần và bước `policy` ghi lại (APM-H-l). Không hạ ngầm: gần hạn mức thì chặn và nói.',
    },
    'ai.policy.max_reasoning_standard': {
        schema: z.enum(['off', 'low', 'medium', 'high', 'adaptive']),
        default: 'adaptive',
        group: 'ai_policy',
        label: 'Trần mức suy nghĩ cho tầng model chuẩn',
        description: 'Tổ chức eo hẹp đặt `medium`: mọi skill trên tầng chuẩn suy nghĩ tối đa `medium`.',
    },
    'ai.policy.max_reasoning_small': {
        schema: z.enum(['off', 'low', 'medium', 'high', 'adaptive']),
        default: 'adaptive',
        group: 'ai_policy',
        label: 'Trần mức suy nghĩ cho tầng model nhỏ',
        description: 'Mặc định skill trên tầng nhỏ chạy `off`; trần này chỉ chặn skill cố xin cao hơn.',
    },
    'ai.context.memory_budget_tokens': {
        schema: z.number().int().min(0).max(20_000),
        default: 1_500,
        group: 'ai_policy',
        label: 'Trần token lớp bộ nhớ trong prompt',
        description: 'Ghi chú bộ nhớ, gợi ý trước cùng target, câu hỏi/trả lời 30 ngày (APM-H-j). Vượt thì cắt theo thứ tự ưu tiên; phần bỏ ghi vào bước `context`.',
    },
    'ai.context.rejection_budget_tokens': {
        schema: z.number().int().min(0).max(5_000),
        default: 500,
        group: 'ai_policy',
        label: 'Trần token riêng cho lý do từ chối của PM',
        description: 'Lý do từ chối gợi ý và ghi chú sinh từ từ chối luôn được chèn trong trần này — không bị ghi chú thường đẩy ra (APM-H-j).',
    },
    'ai.policy.alert_error_rate_pct': {
        schema: z.number().int().min(0).max(100),
        default: 0,
        group: 'ai_policy',
        label: 'Cảnh báo khi tỉ lệ run AI lỗi (6 giờ qua) vượt %',
        description: '0 = tắt. Vượt ⇒ thông báo cho người có `ai_config:manage`, tối đa một lần mỗi 6 giờ cho mỗi ngưỡng. Tắt mặc định — tổ chức đang thử nghiệm sẽ học cách bỏ qua cảnh báo.',
    },
    'ai.policy.alert_cost_per_day_usd': {
        schema: z.number().min(0),
        default: 0,
        group: 'ai_policy',
        label: 'Cảnh báo khi chi phí AI trong ngày vượt (USD)',
        description: '0 = tắt. Tính từ 0h UTC theo `ai_run.cost_usd` của tổ chức.',
    },
    'ai.policy.alert_stuck_runs': {
        schema: z.number().int().min(0).max(1000),
        default: 0,
        group: 'ai_policy',
        label: 'Cảnh báo khi số run AI treo vượt',
        description: '0 = tắt. Run treo là run `running` không có nhịp tim quá 2 × timeout tool × số lượt tối đa.',
    },
    'ai.policy.max_tool_calls_per_turn': {
        schema: z.number().int().min(1).max(32),
        default: 8,
        group: 'ai_policy',
        label: 'Số công cụ gọi song song mỗi lượt',
        description: 'Model đôi khi xin cả chục công cụ trong một lượt. Trần này chặn điều đó.',
    },
    'ai.policy.tool_timeout_ms': {
        schema: z.number().int().min(1_000).max(120_000),
        default: 15_000,
        group: 'ai_policy',
        label: 'Thời gian chờ một công cụ (ms)',
        description: 'Công cụ chạy lâu hơn bị cắt và model được báo để đi đường khác. Không huỷ được truy vấn đang chạy, chỉ trả quyền về vòng lặp.',
    },
    'ai.policy.max_input_tokens': {
        schema: z.number().int().min(1_000),
        default: 150_000,
        group: 'ai_policy',
        label: 'Tổng token vào mỗi lần chạy',
        description: 'Cộng dồn input và cache đọc của mọi lượt. Vượt là ngữ cảnh đang phình — thường do một công cụ trả quá dài.',
    },
    'ai.policy.max_cost_usd': {
        schema: z.number().min(0.001),
        default: 0.5,
        group: 'ai_policy',
        label: 'Chi phí tối đa một lần chạy (USD)',
        description: 'Vượt thì lần chạy kết thúc lỗi và ghi rõ trần nào. Hạn mức theo ngày/tháng ở mục Hạn mức AI là lớp khác.',
    },
    'ai.policy.verify_retries': {
        schema: z.number().int().min(0).max(5),
        default: 2,
        group: 'ai_policy',
        label: 'Số lần cho model sửa kết quả sai',
        description: 'Kết quả sai hình dạng hoặc bị bộ kiểm loại được trả lại cho model sửa bấy nhiêu lần trước khi báo lỗi.',
    },
    /*
      Agent phản biện — APM-CRIT-b (`docs/plan-critic-agent.md` §4.3). Mặc định
      **tắt**: tính năng lên production ở trạng thái 0 vòng và chỉ bật sau khi bench
      (APM-CRIT-d) cho thấy Pass^k tăng vượt sai số chuẩn — bật rồi đo là thứ §5
      của plan-harness cấm. Skill phải khai `critique` mới có gì để bật.
    */
    'ai.policy.critic_rounds': {
        schema: z.number().int().min(0).max(3),
        default: 0,
        group: 'ai_policy',
        label: 'Số vòng agent phản biện tối đa (0 = tắt)',
        description: 'Sau khi kết quả qua Zod, grounding và luật tất định, một model thứ hai đọc bản rút gọn theo checklist của skill và đòi sửa nếu cần. Mỗi vòng thêm một lượt model. Vòng phản biện không trừ "Số lần cho model sửa kết quả sai". Vượt số vòng thì chấp nhận kết quả kèm ý còn lại cho người duyệt.',
    },
    'ai.policy.critic_tier': {
        schema: z.enum(['small', 'standard', 'same']),
        default: 'small',
        group: 'ai_policy',
        label: 'Tầng model của agent phản biện',
        description: 'small: rẻ, đủ cho bài đối chiếu theo checklist. same: cùng tầng với skill — đắt, chỉ khi bench cho thấy small bỏ sót.',
    },
    'ai.policy.critic_other_family': {
        schema: z.boolean(),
        default: true,
        group: 'ai_policy',
        label: 'Phản biện bằng model khác họ',
        description: 'Ưu tiên model khác nhà (Claude ↔ GPT ↔ Gemini) với model vừa lập kết quả, lấy trong chuỗi của tầng phản biện. Giảm thiên lệch "ưa văn phong của chính mình". Không có model khác họ thì dùng cùng họ và bước critique nói ra.',
    },
    'ai.policy.critic_max_cost_usd': {
        schema: z.number().min(0),
        default: 0.02,
        group: 'ai_policy',
        label: 'Chi phí phản biện tối đa một lần chạy (USD)',
        description: 'Chỉ tính lời gọi của người phản biện. Vượt thì dừng vòng và chấp nhận kết quả hiện có.',
    },
    'ai.policy.critic_context_tokens': {
        schema: z.number().int().min(200),
        default: 1_500,
        group: 'ai_policy',
        label: 'Bản rút gọn đưa cho phản biện (token)',
        description: 'Phản biện chỉ thấy bản chiếu của kết quả và dữ liệu liên quan, không thấy lịch sử lượt hay suy nghĩ của model chính. Dài hơn bị cắt.',
    },
    'ai.policy.critic_skills': {
        schema: z.string().max(400),
        default: '*',
        group: 'ai_policy',
        label: 'Skill được phản biện',
        description: 'Tên skill phân cách dấu phẩy, hoặc * cho mọi skill có khai checklist phản biện. Dùng để bật từng skill sau khi bench.',
    },
    'ai.policy.context_budget_tokens': {
        schema: z.number().int().min(200),
        default: 4_000,
        group: 'ai_policy',
        label: 'Ngân sách ngữ cảnh mỗi kết quả công cụ (token)',
        description: 'Kết quả công cụ dài hơn bị cắt có cấu trúc (giữ N phần tử đầu, kèm con trỏ). Công cụ tự phân trang không bị cắt.',
    },
    /*
      Trần LƯU TRỮ của kết quả tool trong dấu vết — APM-H-ap.
  
      Khác `context_budget_tokens` (trần cho **model**): `ai_run_step.tool_result.output`
      là bằng chứng cho người đánh giá, và harness2 đo được rằng đưa bằng chứng đã
      cắt cho người sửa lỗi là người ấy **bịa lỗi**. Nên trace giữ bản nguyên văn,
      và chỉ cắt ở trần này — rộng gấp ~15 lần ngân sách model — kèm cờ
      `truncatedForTrace` để người đọc biết mình đang nhìn một phần.
    */
    'ai.policy.trace_max_result_chars': {
        schema: z.number().int().min(10_000).max(5_000_000),
        default: 200_000,
        group: 'ai_policy',
        label: 'Trần ký tự một kết quả công cụ lưu trong dấu vết',
        description: 'Dấu vết lưu kết quả công cụ nguyên văn (trước khi cắt cho model) tới trần này; vượt thì cắt và đánh dấu `truncatedForTrace`. Không ảnh hưởng thứ model nhận.',
    },
    'ai.policy.sandbox_max_cost_usd': {
        schema: z.number().min(0.001),
        default: 0.2,
        group: 'ai_policy',
        label: 'Chi phí tối đa một lần chạy thử (USD)',
        description: 'Áp cho lượt chạy thử từ trang Harness. Chỉ siết thêm, không nới quá trần thường.',
    },
    'ai.policy.disabled_skills': {
        schema: z
            .string()
            .max(500)
            .regex(/^[a-z0-9_,\s]*$/),
        default: '',
        group: 'ai_policy',
        label: 'Tính năng AI tắt cho tổ chức',
        description: 'Tên skill phân cách bằng dấu phẩy (ví dụ: risk_scan, reschedule). Người dùng vẫn thấy đường làm tay; nút AI của skill đó biến mất.',
    },
    /**
     * Giữ dấu vết lượt chạy AI (`ai_run`, `ai_run_step`) bao lâu — APM-H-a.
     *
     * Dấu vết là dữ liệu chẩn đoán, không phải sự thật nghiệp vụ: chi phí đã có ở
     * `ai_usage_log`, kết quả ở `ai_suggestion`. Ba mươi ngày đủ để điều tra một
     * lời phàn nàn; giữ lâu hơn là giữ nguyên văn dữ liệu dự án trong bảng debug.
     *
     * `0` = giữ mãi.
     */
    'ai.run_retention_days': {
        schema: z.number().int().min(0).max(365),
        default: 30,
        group: 'ai',
        label: 'Giữ dấu vết lượt chạy AI (ngày)',
        description: 'Dấu vết từng bước của các lượt chạy AI cũ hơn số ngày này bị xoá mỗi đêm. Chi phí và kết quả đã duyệt không bị ảnh hưởng. Đặt 0 để giữ mãi.',
    },
    /**
     * Nháp kế hoạch đang chờ người dùng trả lời quá lâu thì tự huỷ — APM-UX-ai3.
     *
     * Một nháp `clarifying` là **việc đang chặn**: cột trợ lý bày nó ra kèm nhãn
     * "chờ bạn trả lời", và nhãn ấy chỉ có nghĩa khi danh sách còn ngắn. Nháp
     * treo một tuần thì mô tả dự án trong nó đã cũ, câu hỏi đã hết thời sự, và
     * trả lời nó cho ra một kế hoạch dựng trên bối cảnh không còn đúng.
     *
     * Huỷ, **không xoá**: `discarded` là một trạng thái thật, đi qua CommandBus
     * và để lại event, nên Replay vẫn dựng đúng lịch sử và người dùng vẫn mở lại
     * xem được mình đã hỏi gì. `0` = không tự huỷ.
     */
    'ai.plan_draft_stale_days': {
        schema: z.number().int().min(0).max(365),
        default: 7,
        group: 'ai',
        label: 'Tự huỷ nháp kế hoạch chờ trả lời (ngày)',
        description: 'Nháp kế hoạch đang chờ bạn trả lời mà không ai đụng tới quá số ngày này sẽ tự chuyển sang trạng thái đã huỷ. Nội dung vẫn đọc lại được. Đặt 0 để không tự huỷ.',
    },
    /** Run `sandbox` (chạy thử từ trang Harness) — dọn sớm hơn, mặc định 7 ngày. */
    'ai.sandbox_run_retention_days': {
        schema: z.number().int().min(0).max(365),
        default: 7,
        group: 'ai',
        label: 'Giữ dấu vết chạy thử AI (ngày)',
        description: 'Lượt chạy thử từ trang Harness cũ hơn số ngày này bị xoá mỗi đêm. Đặt 0 để giữ mãi.',
    },
    /**
     * Giữ thông báo bao lâu rồi dọn — APM-NOTI-l.
     *
     * Hộp thư là **sổ giao hàng dẫn xuất**: mỗi hàng là hệ quả của một event đã
     * có trong `event_log` hoặc của một lượt quét trên state hiện tại, nên xoá đi
     * không mất sự thật nào. Đó chính là lập luận đã dùng để xếp nó vào bảng hạ
     * tầng — và cũng là lý do không cần archive lên S3 như `event_log`.
     *
     * `0` = giữ mãi, cho tổ chức muốn tự dọn tay.
     */
    'notification.retention_days': {
        schema: z.number().int().min(0).max(365),
        default: 90,
        group: 'notification',
        label: 'Giữ thông báo (ngày)',
        description: 'Thông báo cũ hơn số ngày này bị xoá mỗi đêm. Đặt 0 để giữ mãi. Xoá thông báo không mất dữ liệu nào khác: nội dung của nó luôn dựng lại được từ chính việc mà nó nói tới.',
    },
    /**
     * Báo trước bao nhiêu ngày khi task sắp tới hạn — APM-NOTI-h.
     *
     * Dự án đặt riêng được (`project.due_soon_days`); để trống thì **kế thừa** số
     * này. Khác mẫu chép-lúc-tạo của `standard.task_max_days`: đây là **chính
     * sách tổ chức** mà dự án được phép chọn khác, nên đổi ở đây phải có tác dụng
     * ngay với mọi dự án chưa tuỳ chỉnh. Chép lúc tạo thì đổi mặc định không tác
     * dụng gì với dự án đã tồn tại, và không ai hiểu vì sao.
     */
    'notification.due_soon_days': {
        schema: z.number().int().min(1).max(30),
        default: 3,
        group: 'notification',
        label: 'Báo trước hạn (ngày)',
        description: 'Task tới hạn trong khoảng này thì người phụ trách được nhắc. Từng dự án đặt riêng được ở tab Cấu hình; để trống ở đó là dùng số này.',
    },
    'standard.task_max_days': {
        schema: z.number().int().min(1).max(90),
        default: 5,
        group: 'standard',
        label: 'Task dài tối đa (ngày làm việc)',
        description: 'Mặc định cho dự án mới; mỗi dự án chỉnh riêng được. Thực hành Scrum phổ biến: hạng mục lọt gọn trong một sprint và nhìn thấy tiến độ mỗi ngày. Vượt trần chỉ cảnh báo, không chặn.',
    },
    'standard.task_min_hours': {
        schema: z.number().min(0).max(200),
        default: 8,
        group: 'standard',
        label: 'Ước lượng tối thiểu (giờ)',
        description: 'Quy tắc 8/80 của PMBOK, cận dưới. Việc nhỏ hơn thì chi phí theo dõi lớn hơn bản thân việc — gộp vào hạng mục cha thì đúng hơn.',
    },
    'standard.task_max_hours': {
        schema: z.number().min(1).max(2_000),
        default: 80,
        group: 'standard',
        label: 'Ước lượng tối đa (giờ)',
        description: 'Quy tắc 8/80 của PMBOK, cận trên. Việc lớn hơn thì không ai biết nó đang ở đâu cho tới lúc trễ.',
    },
    'standard.task_max_people': {
        schema: z.number().int().min(1).max(20),
        default: 3,
        group: 'standard',
        label: 'Số người tối đa trên một hạng mục',
        description: 'Đếm số người khác nhau được giao các subtask của cùng một hạng mục. Hai người là pair; ba trở lên thì phối hợp tốn hơn phần việc tiết kiệm được.',
    },
    'standard.project_max_months': {
        schema: z.number().int().min(1).max(120),
        default: 12,
        group: 'standard',
        label: 'Dự án dài tối đa (tháng)',
        description: 'Dài hơn thì nên chia thành các giai đoạn có mốc bàn giao riêng — một kế hoạch ba năm không dự đoán được gì ở năm thứ ba.',
    },
    'standard.project_require_objective': {
        schema: z.boolean(),
        default: true,
        group: 'standard',
        label: 'Dự án phải có mục tiêu',
        description: 'Cảnh báo khi một dự án đang chạy mà chưa khai mục tiêu nào. Không có mục tiêu thì không có cách nào nói dự án thành công hay không.',
    },
};
export const SystemSettingKey = z.enum(Object.keys(SYSTEM_SETTINGS));
export const SystemSettingView = z.object({
    key: SystemSettingKey,
    group: z.string(),
    label: z.string(),
    description: z.string(),
    /** Giá trị đang có tác dụng — đã đặt, hoặc mặc định nếu chưa ai đặt. */
    value: z.unknown(),
    defaultValue: z.unknown(),
    /** `false` = đang dùng mặc định. UI hiện điều này để admin biết mình đã đổi gì. */
    isOverridden: z.boolean(),
    updatedAt: z.iso.datetime().nullable(),
});
export const SystemSettingUpdate = z.object({
    key: SystemSettingKey,
    value: z.unknown(),
});
/** Giá trị mặc định có kiểu, cho code phía server đọc mà không cần chạm DB. */
export function defaultSetting(key) {
    return SYSTEM_SETTINGS[key].default;
}
//# sourceMappingURL=settings.js.map